import {
	AcademicEducation,
	AcademicEducationInternals,
	AcademicEducationInternalsInstitution,
	AdditionalFile,
	Address,
	Application,
	ApplicationInternals,
	ApplicationPayment,
	EnglishProof,
	GermanProof,
	PostEvaluation,
	PreparatoryCollege,
	SchoolEducation,
	StudyField,
	WorkExperience
} from '../server/services/application/application.contractpart';
import { isEmpty, nameOf } from './o';

export const SMALL_LETTERS = 'abcdefghijklmnopqrstuvwxyz';
export const BIG_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const NUMBERS = '0123456789';

export function generateSecret(
	length: number = 24,
	possibleCharacters: string[] = [
		SMALL_LETTERS, BIG_LETTERS, NUMBERS, '-_.~',
	],
	ensureOneOfEachCharacterGroups: boolean = false
): string {
	let result: string[] = [];
	const allCharacters = possibleCharacters.join('');
	for (let i: number = 0; i < length; i++) {
		result.push(allCharacters[Math.floor(Math.random() * allCharacters.length)]);
	}
	if (ensureOneOfEachCharacterGroups) {
		for (const group of possibleCharacters) {
			result[Math.floor(Math.random() * length)] = group[Math.floor(Math.random() * group.length)];
		}
	}
	return result.join('');
}

export function generateActivationCode() {
	return ('000000' + Math.trunc(Math.random() * 1000000).toFixed(0)).slice(-6);
}

export function addIfTrue<T>(set: Set<T>, toAdd: T, value: boolean) {
	if (value) {
		set.add(toAdd)
	} else {
		set.delete(toAdd);
	}
}

export function validateEmailAndPasswords(
	email: string,
	password: string,
	password2: string
) {
	const hints: Set<string> = new Set();
	let inputIsValid: boolean;

	addIfTrue(hints, 'please enter your email address', !email);
	addIfTrue(hints, 'please enter a secure password', !password);
	addIfTrue(hints, 'please repeat the password', !password2);
	inputIsValid = hints.size === 0;
	if (inputIsValid) {
		addIfTrue(hints, 'password and repeated password are not equal', password !== password2);
		addIfTrue(hints, 'email not valid', !/\S+?@\S+?\.\S+/.test(email));
		addIfTrue(hints, 'password must be at least 8 characters long', password?.length < 8);
		addIfTrue(hints, 'password must contain at least one numbers', !/[0-9]/.test(password));
		addIfTrue(hints, 'password must contain at least one capital letter', !/[A-Z]/.test(password));
		addIfTrue(hints, 'password must contain at least one lower case letter', !/[a-z]/.test(password));
		addIfTrue(hints, 'password must contain at least one of the following characters: !§$%&=?*+~#\\-_@€', !/[!§$%&=?*+~#\-_@€]/.test(password));
	}
	return [...hints];
}

export function toHex(str?: string): string | undefined {
	if (str === undefined || str === null) {
		return undefined;
	}
	return Array.from(str).map(c => c.charCodeAt(0).toString(16)).join('');
}

export function fromHex(hex?: string): string | undefined {
	if (hex === undefined || hex === null) {
		return undefined;
	}
	return hex
		.match(/.{1,2}/g)
		?.map(byte => String.fromCharCode(parseInt(byte, 16)))
		.join('') || '';
}

export function formatDate(date: Date | number, locale: string = 'en-US'): string {
	if (date === undefined || date === null) {
		return '';
	}
	if (typeof date === 'number') {
		date = new Date(date);
	}
	return new Intl.DateTimeFormat(locale, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	}).format(new Date(date))
}

export function formatDateTime(date: Date | number): string {
	if (date === undefined || date === null) {
		return '';
	}
	if (typeof date === 'number') {
		date = new Date(date);
	}
	return new Intl.DateTimeFormat('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		second: 'numeric',
	}).format(new Date(date))
}

export function toTitleCase(text: string): string {
	return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export function joinNotEmpty(connector: string = ' ', ...args: (string | undefined | null)[]): string {
	return args.filter(n => !isEmpty(n)).join(connector);
}

export function sortBy<T>(list: T[], fn: (x: T) => any, order: 'asc' | 'desc' = 'asc'): T[] {
	list.sort((a, b) => compareBy(a, b, fn) * (order === 'asc' ? 1 : -1));
	return list;
}

export function compareBy<T>(a: T, b: T, fn: (x: T) => any): number {
	if (fn) {
		a = fn(a);
		b = fn(b);
	}
	return a > b ? 1 : a < b ? -1 : 0;
}


//#region functions that in a short and type-save way create field names of applications as needed e.g. for the ApplicationFieldChange service
export function $a(a: keyof Application): string {
	return `${a}`;
}

export function $ac(correspondenceAddressField: keyof Address, asProp: boolean = false): string {
	return `${nameOf<Application>('correspondenceAddress')}${asProp ? '.' : ' '}${correspondenceAddressField}`;
}

export function $ai(academicInternalsField: keyof ApplicationInternals, asProp: boolean = false): string {
	return `${nameOf<Application>('internals')}${asProp ? '.' : ' '}${academicInternalsField}`;
}

export function $as(schoolField: keyof SchoolEducation, asProp: boolean = false): string {
	return `${nameOf<Application>('schoolBackground')}${asProp ? '.' : ' '}${schoolField}`;
}

export function $ap(preparatoryCollegeField: keyof PreparatoryCollege, asProp: boolean = false): string {
	return `${nameOf<Application>('preparatoryCollege')}${asProp ? '.' : ' '}${preparatoryCollegeField}`;
}

export function $apy(paymentField: keyof ApplicationPayment, asProp: boolean = false): string {
	return `${nameOf<Application>('payment')}${asProp ? '.' : ' '}${paymentField}`;
}

export function $ab(index: number, academicBackgroundField: keyof AcademicEducation, asProp: boolean = false): string {
	return `${nameOf<Application>('academicBackground')}${asProp ? '.' : ' '}${index}${asProp ? '.' : ' '}${academicBackgroundField}`;
}

export function $ae(englishProofField: keyof EnglishProof, asProp: boolean = false): string {
	return `${$a('englishGrade')}${asProp ? '.' : ' '}${englishProofField}`;
}

export function $ag(germanProofField: keyof GermanProof, asProp: boolean = false): string {
	return `${$a('germanGrade')}${asProp ? '.' : ' '}${germanProofField}`;
}

export function $abd(index: number, degreeInfoField: keyof StudyField, asProp: boolean = false): string {
	return nameOf<Application>('academicBackground') + (asProp ? '.' : ' ') +
		index + (asProp ? '.' : ' ') +
		nameOf<AcademicEducation>('degreeInfo') + (asProp ? '.' : ' ') +
		degreeInfoField;
}

export function $aaf(index: number, fileField: keyof AdditionalFile, asProp: boolean = false): string {
	return `${nameOf<Application>('additionalFiles')}${asProp ? '.' : ' '}${index}${asProp ? '.' : ' '}${fileField}`;
}

export function $abi(index: number, academicBackgroundInternalsField: keyof AcademicEducationInternals, asProp: boolean = false): string {
	return `${nameOf<Application>('academicBackground')}${asProp ? '.' : ' '}${index}${asProp ? '.' : ' '}${nameOf<AcademicEducation>('internals')}${asProp ? '.' : ' '}${academicBackgroundInternalsField}`;
}

export function $aw(index: number, workBackgroundField: keyof WorkExperience, asProp: boolean = false): string {
	return `${nameOf<Application>('workBackground')}${asProp ? '.' : ' '}` +
		`${index}${asProp ? '.' : ' '}` +
		workBackgroundField;
}

export function $abii(index: number, academicBackgroundInternalsInstitutionField: keyof AcademicEducationInternalsInstitution, asProp: boolean = false): string {
	return `${nameOf<Application>('academicBackground')}${asProp ? '.' : ' '}` +
		`${index}${asProp ? '.' : ' '}` +
		`${nameOf<AcademicEducation>('internals')}${asProp ? '.' : ' '}` +
		`${nameOf<AcademicEducationInternals>('institution')}${asProp ? '.' : ' '}` +
		academicBackgroundInternalsInstitutionField;
}

export function $aip(postEvaluationField: keyof PostEvaluation, asProp: boolean = false): string {
	return $ai('postEvaluation', asProp) + (asProp ? '.' : ' ') +
		postEvaluationField;
}

//#endregion
